import { useEffect, useRef, useState, useCallback } from "react";
import { Trophy, Play } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { EndDishOffer } from "./EndDishOffer";

export const StreetFoodRun = ({ state, refresh }) => {
  const canvasRef = useRef(null);
  const g = useRef(null);
  const rafRef = useRef(0);
  const mascotImg = useRef(null);
  const [dims, setDims] = useState({ w: 640, h: 240 });
  const [phase, setPhase] = useState("idle"); // idle | playing | over
  const [score, setScore] = useState(0);
  const [board, setBoard] = useState(state.runner?.leaderboard || []);
  const [best, setBest] = useState(state.runner?.my_best || 0);
  const [nick, setNick] = useState(state.nickname || "");
  const [nickDraft, setNickDraft] = useState("");
  const [savingNick, setSavingNick] = useState(false);

  useEffect(() => {
    const img = new Image();
    img.src = "/mascot/wave2.png";
    mascotImg.current = img;
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    // phones get a taller play area so the mascot and carts stay visible
    const el = canvasRef.current?.parentElement;
    if (el && el.clientWidth < 520) setDims({ w: 420, h: 300 });
  }, [nick]);

  const saveNick = async () => {
    setSavingNick(true);
    try {
      const r = await api.post("/arcade/nickname", { user_id: USER_ID, nickname: nickDraft });
      setNick(r.data.nickname);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save nickname"); }
    setSavingNick(false);
  };

  const submitScore = useCallback(async (s) => {
    try {
      const r = await api.post("/arcade/runner/score", { user_id: USER_ID, score: s });
      setBoard(r.data.leaderboard);
      setBest(r.data.best);
    } catch { /* score submit is best-effort */ }
  }, []);

  const draw = useCallback(() => {
    const st = g.current;
    const cv = canvasRef.current;
    if (!st || !cv) return;
    const ctx = cv.getContext("2d");
    const { w: W, h: H, ground: GROUND } = st;
    st.frame += 1;
    st.speed = 4.6 + st.frame * 0.0018;
    st.dist += st.speed;
    st.vy += 0.72;
    st.y = Math.min(GROUND, st.y + st.vy);
    if (st.y >= GROUND) { st.vy = 0; st.onGround = true; }
    if (!st.obs.length || st.obs[st.obs.length - 1].x < W - (240 + Math.random() * 240)) {
      const h = 26 + Math.random() * 26;
      st.obs.push({ x: W + 30, w: 26 + Math.random() * 12, h, hot: Math.random() > 0.5 });
    }
    st.obs.forEach((o) => { o.x -= st.speed; });
    st.obs = st.obs.filter((o) => o.x > -60);
    const mx = 58, mw = 30, mh = 46, my = st.y - mh;
    for (const o of st.obs) {
      const oy = GROUND - o.h;
      if (mx + mw - 7 > o.x && mx + 7 < o.x + o.w && my + mh - 4 > oy) {
        const final = Math.floor(st.dist / 12);
        cancelAnimationFrame(rafRef.current);
        g.current = null;
        setScore(final);
        setPhase("over");
        submitScore(final);
        return;
      }
    }
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#0E0C08";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "rgba(212,175,55,0.12)";
    for (let i = 0; i < 8; i++) {
      const bx = ((i * 90 - (st.dist * 0.4) % 90) + 90) % (W + 90) - 45;
      ctx.beginPath(); ctx.moveTo(bx, 18); ctx.lineTo(bx + 10, 34); ctx.lineTo(bx + 20, 18); ctx.fill();
    }
    ctx.strokeStyle = "#D4AF37";
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(0, GROUND + 1); ctx.lineTo(W, GROUND + 1); ctx.stroke();
    ctx.strokeStyle = "rgba(212,175,55,0.25)";
    for (let i = 0; i < 12; i++) {
      const lx = ((i * 60 - st.dist % 60) + 60) % (W + 60) - 30;
      ctx.beginPath(); ctx.moveTo(lx, GROUND + 10); ctx.lineTo(lx + 24, GROUND + 10); ctx.stroke();
    }
    st.obs.forEach((o) => {
      const oy = GROUND - o.h;
      ctx.fillStyle = o.hot ? "#E25822" : "#8a6d1f";
      ctx.fillRect(o.x, oy, o.w, o.h);
      ctx.fillStyle = "#D4AF37";
      ctx.fillRect(o.x - 3, oy - 5, o.w + 6, 5);
      ctx.fillStyle = "#1A1A1A";
      ctx.beginPath(); ctx.arc(o.x + 7, GROUND - 3, 5, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(o.x + o.w - 7, GROUND - 3, 5, 0, Math.PI * 2); ctx.fill();
    });
    const bob = st.onGround ? Math.sin(st.frame * 0.35) * 2 : 0;
    if (mascotImg.current?.complete && mascotImg.current.naturalWidth) {
      ctx.drawImage(mascotImg.current, mx - 8, my - 10 + bob, 46, 58);
    } else {
      ctx.fillStyle = "#F0C742";
      ctx.fillRect(mx, my + bob, mw, mh);
    }
    ctx.fillStyle = "#D4AF37";
    ctx.font = "900 16px sans-serif";
    ctx.textAlign = "right";
    ctx.fillText(String(Math.floor(st.dist / 12)), W - 14, 26);
    rafRef.current = requestAnimationFrame(draw);
  }, [submitScore]);

  const start = useCallback(() => {
    const ground = dims.h - 34;
    g.current = { frame: 0, speed: 4.6, dist: 0, y: ground, vy: 0, onGround: true, obs: [],
      w: dims.w, h: dims.h, ground };
    setPhase("playing");
    setScore(0);
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(draw);
  }, [draw, dims]);

  const jump = useCallback(() => {
    const st = g.current;
    if (!st) { start(); return; }
    if (st.onGround) { st.vy = -12.6; st.onGround = false; }
  }, [start]);

  useEffect(() => {
    const onKey = (e) => { if (e.code === "Space") { e.preventDefault(); jump(); } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [jump]);

  return (
    <div data-testid="game-street-food-run" className="rounded-2xl bg-[#121212] border border-[#E25822]/30 p-5">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <p className="text-[10px] tracking-[0.3em] text-[#E25822] font-bold">PIND FOOD RUN · ਪਿੰਡ ਫੂਡ ਰਨ</p>
          <p className="text-xs text-white/50 mt-1">
            {nick ? "Tap or press space to jump the carts. " : "Save a nickname below, then jump the carts. "}
            Runs earn Regal Coins · weekly #1 wins {state.runner?.mela_first ?? 750} REGAL COINS + the Winner Wall
            {(state.runner?.mela_prizes || []).length ? ` · #2/#3 win ${(state.runner.mela_prizes).join(" / ")} Regal Coins` : ""}.
          </p>
        </div>
        <span data-testid="runner-best" className="text-xs font-black text-[#D4AF37]">BEST THIS WEEK: {best}</span>
      </div>

      {!nick ? (
        <div data-testid="runner-nickname-form" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1C160A] p-4">
          <p className="text-xs text-white/70">Pick an arcade nickname for the leaderboard:</p>
          <div className="flex gap-2 mt-2">
            <input data-testid="runner-nickname-input" value={nickDraft} maxLength={14}
              onChange={(e) => setNickDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && nickDraft.trim().length >= 3 && saveNick()}
              placeholder="e.g. NaanStop"
              className="flex-1 min-w-0 bg-[#1A1A1A] border border-white/15 rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-[#D4AF37]/60" />
            <button data-testid="runner-nickname-save" onClick={saveNick} disabled={savingNick || nickDraft.trim().length < 3}
              className="bg-[#D4AF37] text-black text-[10px] font-black rounded-full px-5 tracking-widest disabled:opacity-40">SAVE</button>
          </div>
        </div>
      ) : (
        <>
          <div className="relative mt-4 rounded-xl overflow-hidden border border-white/10 select-none"
            onPointerDown={(e) => { e.preventDefault(); jump(); }}>
            <canvas ref={canvasRef} width={dims.w} height={dims.h} data-testid="runner-canvas" className="w-full block touch-none" />
            {phase !== "playing" && (
              <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center gap-2 pb-4">
                {phase === "over" && (
                  <p data-testid="runner-final-score" className="font-display text-3xl font-black text-[#D4AF37]">{score}</p>
                )}
                <button data-testid="runner-start"
                  onClick={(e) => { e.stopPropagation(); start(); }}
                  className="flex items-center gap-2 bg-[#E25822] text-white font-black text-xs tracking-widest rounded-full px-6 py-3">
                  <Play size={13} fill="currentColor" /> {phase === "over" ? "RUN AGAIN" : "START RUNNING"}
                </button>
                {phase === "over" && <p className="text-[11px] text-white/60">Score saved as {nick}</p>}
              </div>
            )}
          </div>
          {phase === "over" && <EndDishOffer />}
          <p className="text-[10px] text-white/35 mt-1.5 text-center">TAP THE TRACK OR PRESS SPACE TO JUMP</p>
        </>
      )}

      <div className="mt-4" data-testid="runner-leaderboard">
        <p className="text-[10px] tracking-[0.25em] text-[#D4AF37] font-bold flex items-center gap-1.5"><Trophy size={12} /> THIS WEEK'S TOP 10</p>
        {board.length === 0 ? (
          <p className="text-xs text-white/40 mt-2">No runs yet this week — set the first score!</p>
        ) : (
          <div className="mt-2 divide-y divide-white/5">
            {board.map((r, i) => (
              <div key={`${r.nickname}-${i}`} data-testid={`runner-rank-${i + 1}`} className="flex items-center justify-between py-1.5 text-sm">
                <span className={i < 3 ? "font-black text-[#D4AF37]" : "text-white/70"}>
                  #{i + 1} {r.nickname}{nick && r.nickname === nick ? " (you)" : ""}
                </span>
                <span className="font-mono font-bold">{r.score}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
